import { LoadingButton } from "@mui/lab"
import { Checkbox, Link, Stack, TextField } from "@mui/material"
import { useSnackbar } from "notistack"
import { memo, useState } from "react"
import { z } from "zod"
import { PasswordTextField } from "app/components/PasswordTextField"
import type { OnSignUp } from "interface/types/onSignUp"
import { t } from "lib/i18n"

const zForm = z.object({
  email: z.string(),
  password: z.string(),
})

type Props = {
  isLoading: boolean
  onSignUp: OnSignUp
}

export const HomeRegisterForm = memo<Props>((props) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [termsChecked, setTermsChecked] = useState(false)

  const termsLink = "/terms/tc.html";
  const privacyLink = "/terms/policy.html";

  const { enqueueSnackbar } = useSnackbar()

  const onToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsChecked(event.target.checked);
  }

  const onSubmit = () => {
    try {
      if (password.length < 6) {
        // Firebase Authentication 側でも 6 文字未満を弾くが、UI 側で先に
        // 弾いてラウンドトリップを省く
        throw new Error(t("Password must be at least 6 characters."))
      }
      if (password !== passwordConfirm) {
        throw new Error(t("Password doesn't match"))
      }
      if (!termsChecked) {
        throw new Error(t("Please check the terms and agreements."))
      }

      const form = zForm.parse({ email, password })
      props.onSignUp(form)
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" })
      }
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <Stack spacing={4}>
        <Stack spacing={2}>
          <TextField
            label={t("Email")}
            size={"small"}
            placeholder={t("Email")}
            disabled={props.isLoading}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
            }}
          />
          <PasswordTextField
            label={t("Password")}
            size={"small"}
            placeholder={t("Password")}
            autoComplete="password"
            disabled={props.isLoading}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
            }}
          />
          <PasswordTextField
            label={t("Password confirm")}
            size={"small"}
            placeholder={t("Password confirm")}
            autoComplete="password"
            disabled={props.isLoading}
            value={passwordConfirm}
            onChange={(event) => {
              setPasswordConfirm(event.target.value)
            }}
          />
        </Stack>
        <div style={{ fontSize: '0.8em', marginTop: '5px' }}>
          <Checkbox size="small" onChange={onToggle} />{t("I agree to the terms of the")} <Link href={termsLink} variant="inherit">{t("Subscription Agreement")} </Link> & <Link href={privacyLink} variant="inherit">{t("Privacy Policy")}</Link>.
        </div>
        <LoadingButton
          type={"submit"}
          variant="contained"
          loading={props.isLoading}
          disabled={!termsChecked}
        >
          {t("Sign Up")}
        </LoadingButton>
      </Stack>
    </form>
  )
})
